import { SyntiroHoneyConfidenceBand } from '../../../types/syntiro_honey';

/** Lower bound (inclusive) of the high confidence band. */
export const HIGH_CONFIDENCE_THRESHOLD = 0.9;

/** Lower bound (inclusive) of the medium confidence band. */
export const MEDIUM_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Derive the confidence band from a system-computed confidence score.
 * Bands per the Honey contract: high >= 0.90, medium >= 0.70, low < 0.70.
 * @param {number} confidence System-computed confidence in [0, 1].
 * @returns {SyntiroHoneyConfidenceBand}
 */
export const deriveConfidenceBand = (confidence: number): SyntiroHoneyConfidenceBand => {
  if (confidence >= HIGH_CONFIDENCE_THRESHOLD) {
    return 'high';
  }
  if (confidence >= MEDIUM_CONFIDENCE_THRESHOLD) {
    return 'medium';
  }
  return 'low';
};

/** MUI palette colour used to render each confidence band. */
export const CONFIDENCE_BAND_COLOR: Record<
  SyntiroHoneyConfidenceBand,
  'success' | 'warning' | 'error'
> = {
  high: 'success',
  medium: 'warning',
  low: 'error'
};

/**
 * Reviewer-facing explanation per band: what the band means and what to do,
 * deliberately GENERIC — no sample sizes, reference sets, or eval history
 * (Maxine 2026-08-18: "keep it generic to the user, do not go into technical
 * details"). The calibration behind the bands remains internal: measured against
 * the CCH reference golden via `yarn eval:cch` (2026-06-23, deterministic path) —
 * HIGH = 123/123 metric + value correct; MEDIUM = 0 rows; LOW = 108 rows whose
 * metric mapped correctly but whose value was unscoreable (blank reference value).
 * Refresh that calibration as the golden grows, but keep it out of the copy.
 */
export const CONFIDENCE_BAND_ACCURACY_REFERENCE: Record<SyntiroHoneyConfidenceBand, string> = {
  high: 'The system is confident this value matches the source document — please still give it a quick check.',
  medium: 'The system is only partly confident in this value — please check it against the source.',
  low: 'The system could not verify this value — please check it against the source.'
};
